import { CONSTANTS } from '../data';
import { GameState } from '../systems/GameState';
import { typeAIMessage } from '../utils/typeText';

export interface Achievement {
  id: string;
  name: string;
  description: string;
  unlocks?: string;
}

export class AchievementSystem {
  private gameState = GameState.getInstance();

  public unlockAchievement(
    id: string,
    unlockedAchievements: Set<string>,
    setUnlockedAchievements: React.Dispatch<React.SetStateAction<Set<string>>>,
    discoveredCombinations: Set<string>,
    setDiscoveredCombinations: React.Dispatch<React.SetStateAction<Set<string>>>,
    setAchievementPopup: React.Dispatch<React.SetStateAction<{id: string, name: string, description: string} | null>>,
    setGameState: React.Dispatch<React.SetStateAction<string>>,
    shownChapters: Set<string>,
    shownMoralEvents: Set<string>
  ) {
    if (unlockedAchievements.has(id)) return;

    const achievement = (CONSTANTS.ACHIEVEMENTS as Record<string, any>)[id] as Achievement;
    if (!achievement) return;

    console.log(`Achievement unlocked: ${achievement.name}`);
    const newAchievements = new Set(unlockedAchievements).add(id);
    setUnlockedAchievements(newAchievements);
    
    setAchievementPopup({ id: achievement.id, name: achievement.name, description: achievement.description });
    setTimeout(() => setAchievementPopup(null), 5000);

    // Generic unlock handler
    if (achievement.unlocks && !discoveredCombinations.has(achievement.unlocks)) {
      setDiscoveredCombinations(prev => new Set(prev).add(achievement.unlocks as string));
      typeAIMessage(`Your actions have resulted in a new synthesis: ${achievement.unlocks}.`, discoveredCombinations);
    }

    switch (id) {
      case 'first_combination':
        typeAIMessage(CONSTANTS.AI_INTERACTIONS.responses.achievement_unlocks.first_combination, discoveredCombinations);
        break;
      case 'ai_awakening':
        typeAIMessage(CONSTANTS.AI_INTERACTIONS.responses.achievement_unlocks.ai_awakening, discoveredCombinations);
        break;
      case 'all_combinations':
        setTimeout(() => setGameState('secret_ending'), 2000);
        break;
    }
    
    this.gameState.saveGame(discoveredCombinations, newAchievements, shownChapters, shownMoralEvents);
  }

  public checkAchievements(
    discoveredCombinations: Set<string>,
    unlockedAchievements: Set<string>,
    setUnlockedAchievements: React.Dispatch<React.SetStateAction<Set<string>>>,
    setDiscoveredCombinations: React.Dispatch<React.SetStateAction<Set<string>>>,
    setAchievementPopup: React.Dispatch<React.SetStateAction<{id: string, name: string, description: string} | null>>,
    setGameState: React.Dispatch<React.SetStateAction<string>>,
    shownChapters: Set<string>,
    shownMoralEvents: Set<string>
  ) {
    const discoveredCount = discoveredCombinations.size;

    if (discoveredCount >= 1 && !unlockedAchievements.has('first_combination')) {
      this.unlockAchievement('first_combination', unlockedAchievements, setUnlockedAchievements, discoveredCombinations, setDiscoveredCombinations, setAchievementPopup, setGameState, shownChapters, shownMoralEvents);
    }

    if (discoveredCount >= 10 && !unlockedAchievements.has('ten_combinations')) {
      this.unlockAchievement('ten_combinations', unlockedAchievements, setUnlockedAchievements, discoveredCombinations, setDiscoveredCombinations, setAchievementPopup, setGameState, shownChapters, shownMoralEvents);
    }

    if (this.gameState.selfAwareTriggered && !unlockedAchievements.has('ai_awakening')) {
      this.unlockAchievement('ai_awakening', unlockedAchievements, setUnlockedAchievements, discoveredCombinations, setDiscoveredCombinations, setAchievementPopup, setGameState, shownChapters, shownMoralEvents);
    }

    if (discoveredCount >= CONSTANTS.TOTAL_COMBINATIONS && !unlockedAchievements.has('all_combinations')) {
      this.unlockAchievement('all_combinations', unlockedAchievements, setUnlockedAchievements, discoveredCombinations, setDiscoveredCombinations, setAchievementPopup, setGameState, shownChapters, shownMoralEvents);
    }
  }
}
